"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useFindPickupPointsQuery = void 0;
var _fetching = require("@ori/fetching");
var _react = require("react");
var _constants = require("../constants");
var _swrConfig = require("../constants/swrConfig");
var _hooks = require("../hooks");
var _useGeocodingSearch = require("../hooks/useGeocodingSearch");
var _logger = require("../logger");
var _utils = require("../utils");
const dataUrl = '/system/ajax/DeliverySelection/FindPickupPoints';
const hookName = 'useFindPickupPointsQuery';
const catchError = async (promise, description) => (0, _utils.catchPromiseError)(promise, `${description} error in ${hookName} hook`);
const fetchData = async (options, description) => {
  (0, _logger.logInfo)(`${hookName} hook - ${description}: fetching data ...\n\toptions:`, options);
  const promise = catchError((0, _utils.getUrl)(options), description);
  const data = await promise;
  (0, _logger.logInfo)(`${hookName} hook - ${description}: loaded data:`, data);
  return promise;
};
const fetchBaseQueryData = async options => fetchData(options, 'Base data query');
const fetchFallbackQueryData = async options => fetchData(options, 'Fallback data query');

/**
 * @obsolete Can be dropped later when flat selection UI is used in all markets
 * and/or the merged map & list UI switch is enabled everywhere.
 */
const useFindPickupPointsQuery = options => {
  (0, _logger.logDebug)(`${hookName} hook called, params:`, options);
  const [isLoadMoreVisible, setIsLoadMoreVisible] = (0, _react.useState)(false);
  const swrOptions = {
    ..._swrConfig.errorHandlingSwrOptions,
    onSuccess: data => {
      (0, _logger.logInfo)(`${hookName} hook - loading data finished successfully.\n\t`, data);
      setIsLoadMoreVisible((data ?? []).length > _constants.PICKUP_POINTS_PAGE_SIZE);
    }
  };
  const {
    activeFilter: filter,
    isMapEnabled,
    page,
    searchTerm
  } = options;
  const skip = (Number.isNaN(page) ? 0 : page) * _constants.PICKUP_POINTS_PAGE_SIZE;
  const top = _constants.PICKUP_POINTS_PAGE_SIZE + 1;
  const baseQueryParams = {
    filter,
    searchTerm,
    skip,
    top
  };
  const {
    cacheKeys: baseQueryCacheKeys,
    urlOptions: baseQueryUrlOptions
  } = (0, _hooks.useUrlOptions)(dataUrl, baseQueryParams);

  // base query - run only when searchTerm is set
  const {
    data: baseQueryData,
    error: baseQueryError,
    isValidating: isBaseQueryLoading
  } = (0, _fetching.useSWR)(() => searchTerm ? baseQueryCacheKeys : null, async () => fetchBaseQueryData(baseQueryUrlOptions), swrOptions);
  const isEmptyBaseQueryResult = Boolean(searchTerm && baseQueryData && !baseQueryData.length);
  const isGeocodingSearchEnabled = isEmptyBaseQueryResult && isMapEnabled;
  (0, _logger.logDebug)(`${hookName} hook: isEmptyBaseQueryResult:`, isEmptyBaseQueryResult, 'isGeocodingSearchEnabled:', isGeocodingSearchEnabled, 'isMapEnabled', isMapEnabled);

  // query to get coordinates for fallback search - run when searchTerm is set and base query returned 0 results
  const geocoding = (0, _useGeocodingSearch.useGeocodingSearch)({
    isActive: isGeocodingSearchEnabled,
    searchTerm
  });
  (0, _logger.logDebug)(`${hookName} hook: isEmptyBaseQueryResult && Boolean(firstItemCoordinates)`, isEmptyBaseQueryResult && Boolean(geocoding.data));

  // fallback search query for pickup points near the received coordinates - run when coordinates are defined
  const {
    cacheKeys: fallbackQueryCacheKeys,
    urlOptions: fallbackQueryUrlOptions
  } = (0, _hooks.useUrlOptions)(dataUrl, {
    filter,
    searchTerm: geocoding.data ? `${geocoding.data.latitude};${geocoding.data.longitude}` : '',
    skip,
    top
  });
  const {
    data: fallbackQueryData,
    error: fallbackQueryError,
    isValidating: isFallbackQueryLoading
  } = (0, _fetching.useSWR)(() => isEmptyBaseQueryResult && Boolean(geocoding.data) ? fallbackQueryCacheKeys : null, async () => fetchFallbackQueryData(fallbackQueryUrlOptions), swrOptions);

  // geocoding implementation might be loading at start, so checking searchTerm is needed
  const isPickupPointsListLoading = Boolean(searchTerm) && (isBaseQueryLoading || geocoding.isLoading || isFallbackQueryLoading);
  const pickupPointsList = (baseQueryData ?? []).length > 0 ? baseQueryData : fallbackQueryData ?? [];
  const pickupPointsListError = baseQueryError ?? geocoding.error ?? fallbackQueryError;
  (0, _logger.logDebug)(`${hookName} hook:\n\tlocal vars:`, {
    isPickupPointsListLoading,
    isBaseQueryLoading,
    isFallbackQueryLoading,
    pickupPointsList
  });
  return {
    foundPlaceLabel: geocoding.data?.label,
    isLoadMoreVisible,
    isPickupPointsListLoading,
    pickupPointsList,
    pickupPointsListError
  };
};
exports.useFindPickupPointsQuery = useFindPickupPointsQuery;